import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Badge,
} from "@wondermaps/ui";
import { useTranslation, Trans } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldCheck } from "@fortawesome/pro-solid-svg-icons/faShieldCheck";
import { faShieldXmark } from "@fortawesome/pro-solid-svg-icons/faShieldXmark";
import { faShieldExclamation } from "@fortawesome/pro-solid-svg-icons/faShieldExclamation";
import type { ConsentStatus } from "../../hooks/useAnalyticsConsent";

interface ConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: ConsentStatus;
  onAccept: () => void;
  onDecline: () => void;
}

const STATUS_ICONS = {
  accepted: {
    icon: faShieldCheck,
    key: "consent.statusAccepted",
    variant: "default" as const,
  },
  declined: {
    icon: faShieldXmark,
    key: "consent.statusDeclined",
    variant: "secondary" as const,
  },
  pending: {
    icon: faShieldExclamation,
    key: "consent.statusNotSet",
    variant: "outline" as const,
  },
};

/**
 * Modal for viewing/changing analytics consent preferences.
 * Accessible from visitor menu for users who want to change their choice.
 */
export function ConsentModal({
  open,
  onOpenChange,
  status,
  onAccept,
  onDecline,
}: ConsentModalProps) {
  const { t } = useTranslation("common");
  const statusConfig = STATUS_ICONS[status];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consent-modal"
        className="max-w-md rounded-xl w-[98%]"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("consent.cookiePreferences")}
          </DialogTitle>
          <DialogDescription>{t("consent.manageAnalytics")}</DialogDescription>
        </DialogHeader>

        {/* Current status */}
        <div className="flex items-center gap-2 py-2">
          <span className="text-sm text-muted-foreground">
            {t("consent.currentStatus")}
          </span>
          <Badge variant={statusConfig.variant} className="gap-1">
            <FontAwesomeIcon icon={statusConfig.icon} className="h-3 w-3" />
            {t(statusConfig.key)}
          </Badge>
        </div>

        {/* What we collect / don't collect */}
        <div className="space-y-3 rounded-lg bg-muted/50 px-4 text-sm">
          <div>
            <p className="font-medium text-foreground">
              {t("consent.whatWeCollect")}
            </p>
            <ul className="mt-1 list-inside list-disc text-muted-foreground">
              <li>{t("consent.collectPages")}</li>
              <li>{t("consent.collectFeatures")}</li>
              <li>{t("consent.collectDevice")}</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-foreground">
              {t("consent.whatWeDontCollect")}
            </p>
            <ul className="mt-1 list-inside list-disc text-muted-foreground">
              <li>{t("consent.noLocation")}</li>
              <li>{t("consent.noPersonalInfo")}</li>
              <li>{t("consent.noAdvertisers")}</li>
            </ul>
          </div>
        </div>

        {/* Privacy policy link */}
        <p className="text-sm text-muted-foreground">
          <Trans
            i18nKey="consent.privacyPolicy"
            ns="common"
            components={[
              <span key="0" />,
              <a
                key="1"
                href="https://www.wondermaps.co/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              />,
            ]}
          />
        </p>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onDecline}>
            {t("decline")}
          </Button>
          <Button onClick={onAccept}>{t("accept")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
